import { useEffect, useState, useRef } from "react";
import searchPnc from "../../assets/searchPnc.svg";
import folderPnc from "../../assets/folderPnc.svg";
import dropPnc from "../../assets/dropPnc.svg";
import fusoChat from "../../assets/fusoChat.svg";
import "./SearchParameters.css";
import { CHAT_API_ENDPOINTS } from "../../api/CHAT_API_ENDPOINTS";
import { useTranslation } from "react-i18next";

const SearchParameters = ({
  searchType,
  setSearchType,
  vinInput,
  handleVinInputChange,
  isLoadingVin,
  vinError,
  modelInput,
  classificationInput,
  handleModelInputChange,
  handleClassificationInputChange,
  isLoadingModelClass,
  modelClassError,
  isSearchValid,
  groups,
  subGroups,
  selectedGroup,
  selectedGroupMainGroup,
  selectedSubGroup,
  selectedSubGroupMainGroup,
  groupDropdownOpen,
  subGroupDropdownOpen,
  setGroupDropdownOpen,
  setSubGroupDropdownOpen,
  handleGroupSelect,
  handleSubGroupSelect,
  isLoadingSubGroups,
  resetSearchState,
  // key,
  cycleId,
  isNewCycle = false,
  onSelectionChange,
  onValidationChange,
  preserveValues = false,
}) => {
  const { t } = useTranslation();
  const instanceId = `${cycleId}-search-params`;

  // Modal states for mobile view
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [subGroupModalOpen, setSubGroupModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Publication input state
  const [publicationNumberInput, setPublicationNumberInput] = useState("");

  // Model autocomplete state
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);
  const [isLoadingModelSuggestions, setIsLoadingModelSuggestions] = useState(false);
  const modelInputRef = useRef(null);
  const modelSuggestionsRef = useRef(null);

  // VIN autocomplete state
  const [vinSuggestions, setVinSuggestions] = useState([]);
  const [showVinSuggestions, setShowVinSuggestions] = useState(false);
  const [isLoadingVinSuggestions, setIsLoadingVinSuggestions] = useState(false);
  const vinInputRef = useRef(null);
  const vinSuggestionsRef = useRef(null);
  const [noVinResultsFound, setNoVinResultsFound] = useState(false);
  const [isVinSelected, setIsVinSelected] = useState(false);

  const [noModelResultsFound, setNoModelResultsFound] = useState(false);
  const [isModelSelected, setIsModelSelected] = useState(false);

  const [
    isLoadingPublicationNumberSuggestions,
    setIsLoadingPublicationNumberSuggestions,
  ] = useState(false);
  const [noPublicationNumberResultsFound, setNoPublicationNumberResultsFound] =
    useState(false);
  const [isPublicationNumberSelected, setIsPublicationNumberSelected] =
    useState(false);

  // Publication number list for autocomplete
  const publicationNumberList = [
    "00ELT0211",
    "00ELT0212",
    "00ELT0213",
    "00ELT0266",
    "00ELT0267",
    "00ELT0268",
    "00ELT0269",
    "00ELT0270",
    "00ELT0312",
    "00ELT0313",
    "00ELT0314",
    "00ELT0315",
    "00ELT0316",
  ];

  // Publication number autocomplete state
  const [publicationNumberSuggestions, setPublicationNumberSuggestions] =
    useState([]);
  const [
    showPublicationNumberSuggestions,
    setShowPublicationNumberSuggestions,
  ] = useState(false);
  const publicationNumberInputRef = useRef(null);
  const publicationNumberSuggestionsRef = useRef(null);

  // Publication types for dropdown
  const publicationTypes = [
    { id: 1, name: "Chassis" },
    { id: 2, name: "Electrical" },
    { id: 3, name: "Electronic Control" },
    { id: 4, name: "Engine" },
    { id: 5, name: "General" },
  ];

  const [selectedPublication, setSelectedPublication] = useState(null);
  const [publicationDropdownOpen, setPublicationDropdownOpen] = useState(false);
  const [publicationModalOpen, setPublicationModalOpen] = useState(false);

  // Dynamic countries and dates from API
  const [dynamicCountriesJP, setDynamicCountriesJP] = useState([]);
  const [dynamicCountriesEN, setDynamicCountriesEN] = useState([]);
  const [dynamicMonthYearsJP, setDynamicMonthYearsJP] = useState([]);
  const [dynamicMonthYearsEN, setDynamicMonthYearsEN] = useState([]);
  const [modelDetailsData, setModelDetailsData] = useState(null);
  const [countryNameMapping, setCountryNameMapping] = useState({});

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countryModalOpen, setCountryModalOpen] = useState(false);

  // Calendar states for month/year selection
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);

  // Months array for dropdown
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Generate array of years (current year and 10 years before)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  // Refs for all dropdowns to detect clicks outside
  const publicationDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const groupDropdownRef = useRef(null);
  const subGroupDropdownRef = useRef(null);

  // API call for VIN suggestions
  const fetchVinSuggestions = async (vinValue) => {
  // Trim spaces from the beginning and end of the VIN value
  const trimmedVinValue = vinValue ? vinValue.trim() : "";

  if (!trimmedVinValue || trimmedVinValue.length < 1) {
    setVinSuggestions([]);
    setShowVinSuggestions(false);
    setNoVinResultsFound(false);
    setIsVinSelected(false);

    // Always validate if no VIN is entered
    if (onValidationChange) {
      onValidationChange(true);
    }
    return;
  }

  setIsLoadingVinSuggestions(true);
  setNoVinResultsFound(false);

  try {
    // Use the trimmed VIN value for the API call
    const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_VIN_SEARCH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        vin_keyword: trimmedVinValue, // Send trimmed value to API
      }),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();

    // Handle the specific response structure with vin_list array
    let formattedData = [];
    if (data.vin_list && Array.isArray(data.vin_list)) {
      formattedData = data.vin_list;
    } else if (Array.isArray(data)) {
      formattedData = data;
    } else if (data.results && Array.isArray(data.results)) {
      formattedData = data.results;
    } else if (data.data && Array.isArray(data.data)) {
      formattedData = data.data;
    } else if (typeof data === "object") {
      formattedData = [data];
    }

    setVinSuggestions(formattedData);

    // Check if the trimmed VIN matches any suggestion
    const isVinMatching = formattedData.some((vin) => {
      const vinValueToCheck =
        typeof vin === "string"
          ? vin
          : vin.vinNumber ||
            vin.vin ||
            vin.chassis_no ||
            vin.chassis_number ||
            JSON.stringify(vin);

      // Compare with trimmed values for both sides
      return (
        vinValueToCheck.toLowerCase().trim() === trimmedVinValue.toLowerCase()
      );
    });

    // FIXED: Only show validation message when NO suggestions are returned
    if (formattedData.length === 0) {
      // API returned no results - show "not found" message
      setNoVinResultsFound(true);
      setShowVinSuggestions(false);
      setIsVinSelected(false);

      // No suggestions, validation fails
      if (onValidationChange) {
        onValidationChange(true);
      }
    } else {
      // API returned results - NEVER show validation message
      setNoVinResultsFound(false);
      setShowVinSuggestions(true);

      // Check for exact match but don't show validation message
      if (isVinMatching) {
        setIsVinSelected(true);
        if (onValidationChange) {
          onValidationChange(false); // Valid - exact match
        }
      } else {
        setIsVinSelected(false);
        if (onValidationChange) {
          onValidationChange(false); // Valid - has suggestions even if not exact match
        }
      }
    }
  } catch (error) {
    console.error("Error fetching VIN suggestions:", error);

    // Show error message only after API fails
    setNoVinResultsFound(true);
    setShowVinSuggestions(false);
    setIsVinSelected(false);

    // Error means no valid selection possible
    if (onValidationChange) {
      onValidationChange(true);
    }
  } finally {
    setIsLoadingVinSuggestions(false);
  }
};


  // Handle VIN input change with API call for suggestions
  const handleVinInputChangeWithSuggestions = (e) => {
  const value = e.target.value;

  // Call the original handler from props with the original value (keeping spaces for display)
  if (handleVinInputChange) {
    handleVinInputChange(e);
  }

  // Reset validation states but don't trigger validation error yet
  setIsVinSelected(false);
  setNoVinResultsFound(false);
  
  // Only set validation to true (valid) if input is empty - this prevents sending
  if (onValidationChange) {
    onValidationChange(value.trim().length === 0);
  }

  // Fetch suggestions from API (this will handle validation inside)
  fetchVinSuggestions(value);
};


  const fetchPublicationNumberSuggestions = async (publicationValue) => {
  if (!publicationValue || publicationValue.length < 1) {
    setPublicationNumberSuggestions([]);
    setShowPublicationNumberSuggestions(false);
    setNoPublicationNumberResultsFound(false);
    setIsPublicationNumberSelected(false);

    // Always validate if no publication number is entered
    if (onValidationChange) {
      onValidationChange(true);
    }
    return;
  }

  setIsLoadingPublicationNumberSuggestions(true);
  setNoPublicationNumberResultsFound(false);

  try {
    // Use an API endpoint for publication numbers
    const response = await fetch(
      CHAT_API_ENDPOINTS.SHOP_MANUAL_PUBLICATION_SEARCH,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          publication_number_keyword: publicationValue,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();

    // Handle the specific response structure with publication_number_list
    let formattedData = [];
    if (
      data.publication_number_list &&
      Array.isArray(data.publication_number_list)
    ) {
      formattedData = data.publication_number_list;
    } else if (
      data.publication_list &&
      Array.isArray(data.publication_list)
    ) {
      formattedData = data.publication_list;
    } else if (Array.isArray(data)) {
      formattedData = data;
    } else if (data.results && Array.isArray(data.results)) {
      formattedData = data.results;
    } else if (data.data && Array.isArray(data.data)) {
      formattedData = data.data;
    } else if (typeof data === "object") {
      formattedData = [data];
    }

    setPublicationNumberSuggestions(formattedData);

    // Check if the entered Publication Number matches any suggestion
    const isPublicationNumberMatching = formattedData.some((pubNumber) => {
      const pubNumberValue =
        typeof pubNumber === "string" ? pubNumber : String(pubNumber);
      return pubNumberValue.toLowerCase() === publicationValue.toLowerCase();
    });

    // FIXED: Only show validation message when NO suggestions are returned
    if (formattedData.length === 0) {
      // API returned no results - show "not found" message
      setNoPublicationNumberResultsFound(true);
      setShowPublicationNumberSuggestions(false);
      setIsPublicationNumberSelected(false);

      // No suggestions, validation fails
      if (onValidationChange) {
        onValidationChange(true);
      }
    } else {
      // API returned results - NEVER show validation message
      setNoPublicationNumberResultsFound(false);
      setShowPublicationNumberSuggestions(true);

      // Check for exact match but don't show validation message
      if (isPublicationNumberMatching) {
        setIsPublicationNumberSelected(true);
        if (onValidationChange) {
          onValidationChange(false); // Valid - exact match
        }
      } else {
        setIsPublicationNumberSelected(false);
        if (onValidationChange) {
          onValidationChange(false); // Valid - has suggestions even if not exact match
        }
      }
    }
  } catch (error) {
    console.error("Error fetching Publication Number suggestions:", error);

    // Show error message only after API fails
    setNoPublicationNumberResultsFound(true);
    setShowPublicationNumberSuggestions(false);
    setIsPublicationNumberSelected(false);

    // Error means no valid selection possible
    if (onValidationChange) {
      onValidationChange(true);
    }
  } finally {
    setIsLoadingPublicationNumberSuggestions(false);
  }
};


  // Handle VIN suggestion selection
  const handleVinSuggestionClick = (vin) => {
    // Handle string or object type for VIN
    const vinValue =
      typeof vin === "string"
        ? vin
        : vin.vinNumber ||
          vin.vin ||
          vin.chassis_no ||
          vin.chassis_number ||
          JSON.stringify(vin);

    // Trim the selected VIN value
    const trimmedVinValue = vinValue.trim();

    const event = {
      target: { value: trimmedVinValue }, // Use trimmed value
    };

    if (handleVinInputChange) {
      handleVinInputChange(event);
    }

    setShowVinSuggestions(false);
    setNoVinResultsFound(false);

    // Mark as selected since user picked from list
    setIsVinSelected(true);

    // Since user selected from list, validation passes
    if (onValidationChange) {
      onValidationChange(false);
    }

    // Focus back on input
    if (vinInputRef.current) {
      vinInputRef.current.focus();
    }
  };

  // API call for Model suggestions
  const fetchModelSuggestions = async (modelValue) => {
    if (!modelValue || modelValue.length < 1) {
      setModelSuggestions([]);
      setShowModelSuggestions(false);
      setNoModelResultsFound(false);
      setIsModelSelected(false);

      // Always validate if no model is entered
      if (onValidationChange) {
        onValidationChange(true);
      }
      return;
    }

    setIsLoadingModelSuggestions(true);
    setNoModelResultsFound(false);

    try {
      const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_MODEL_SUGGESTION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model_keyword: modelValue,
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      // Handle the specific response structure
      let formattedData = [];
      if (data.model_list && Array.isArray(data.model_list)) {
        formattedData = data.model_list;
      } else if (Array.isArray(data)) {
        formattedData = data;
      } else if (data.results && Array.isArray(data.results)) {
        formattedData = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        formattedData = data.data;
      } else if (typeof data === "object") {
        formattedData = [data];
      }

      setModelSuggestions(formattedData);

      // Check if the entered model matches any suggestion exactly
      const exactMatch = formattedData.find((model) => {
        const modelValueStr = typeof model === "string" ? model : String(model);
        return modelValueStr.toUpperCase() === modelValue.toUpperCase();
      });

      // Only show validation message when NO suggestions are returned
      if (formattedData.length === 0) {
        // API returned no results - show "not found" message
        setNoModelResultsFound(true);
        setShowModelSuggestions(false);
        setIsModelSelected(false);

        // No suggestions, validation fails
        if (onValidationChange) {
          onValidationChange(true);
        }
      } else {
        // API returned results - NEVER show validation message
        setNoModelResultsFound(false);

        // Always show suggestions when we have results
        setShowModelSuggestions(true);

        // Check for exact match
        if (exactMatch) {
          setIsModelSelected(true);
          if (onValidationChange) {
            onValidationChange(false); // Valid - exact match
          }

          // Reset dependent fields when a new model is typed
          setSelectedPublication(null);
          setSelectedCountry(null);
          setSelectedMonth(null);
          setSelectedYear(null);

          console.log("Exact model match found - Auto-fetching model details");

          // Automatically fetch model details for exact match
          await fetchModelDetails(modelValue);
        } else {
          setIsModelSelected(false);
          if (onValidationChange) {
            onValidationChange(false); // Valid - has suggestions even if not exact match
          }
        }
      }
    } catch (error) {
      console.error("Error fetching Model suggestions:", error);

      // Show error message only after API fails
      setNoModelResultsFound(true);
      setShowModelSuggestions(false);
      setIsModelSelected(false);

      // Error means no valid selection possible
      if (onValidationChange) {
        onValidationChange(true);
      }
    } finally {
      setIsLoadingModelSuggestions(false);
    }
  };

  // Handle model input change with autocomplete
  const handleModelInputChangeWithSuggestions = (e) => {
    const value = e.target.value;

    // Call the original handler
    handleModelInputChange(e);

    // Reset validation states but don't show error immediately
    setIsModelSelected(false);
    setNoModelResultsFound(false);

    // Only set validation to true if input is empty
    if (onValidationChange) {
      onValidationChange(value.trim().length === 0);
    }

    // Fetch suggestions from API
    fetchModelSuggestions(value);
  };

  // Handle model input click to show suggestions
  const handleModelInputClick = () => {
    const currentValue = modelInput.trim();

    // If there's a value, fetch and show suggestions
    if (currentValue.length > 0) {
      // Show existing suggestions if available
      if (modelSuggestions.length > 0) {
        setShowModelSuggestions(true);
      } else {
        // Fetch suggestions from API
        fetchModelSuggestions(currentValue);
      }
    }
  };

  // Handle paste event for model input
  const handleModelPaste = async (e) => {
    // Get pasted text
    const pastedText = e.clipboardData.getData('text').trim();

    if (pastedText) {
      // Prevent default paste behavior
      e.preventDefault();

      // Set the input value
      const event = {
        target: { value: pastedText }
      };
      handleModelInputChange(event);

      // Reset validation states
      setIsModelSelected(false);
      setNoModelResultsFound(false);

      // Fetch suggestions and check for match
      await fetchModelSuggestions(pastedText);

      // After fetching suggestions, check if there's an exact match
      setTimeout(async () => {
        const response = await fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_MODEL_SUGGESTION, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model_keyword: pastedText,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          let formattedData = [];
          if (data.model_list && Array.isArray(data.model_list)) {
            formattedData = data.model_list;
          }

          // Check for exact match
          const exactMatch = formattedData.find((model) => {
            const modelValue = typeof model === "string" ? model : String(model);
            return modelValue.toUpperCase() === pastedText.toUpperCase();
          });

          // If exact match found, fetch model details automatically
          if (exactMatch) {
            setIsModelSelected(true);
            setShowModelSuggestions(false);
            if (onValidationChange) {
              onValidationChange(false);
            }

            // Reset dependent fields
            setSelectedPublication(null);
            setSelectedCountry(null);
            setSelectedMonth(null);
            setSelectedYear(null);

            // Fetch model details
            await fetchModelDetails(pastedText);
          }
        }
      }, 100);
    }
  };


  // Function to fetch model details when a model is selected
  const fetchModelDetails = async (modelKeyword) => {
    try {
      // Call both APIs in parallel - one for Japanese, one for English
      const [japaneseResponse, englishResponse] = await Promise.all([
        fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_MODEL_DETAILS, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model_keyword: modelKeyword,
            language: "jp",
          }),
        }),
        fetch(CHAT_API_ENDPOINTS.SHOP_MANUAL_MODEL_DETAILS, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model_keyword: modelKeyword,
            language: "en",
          }),
        }),
      ]);

      // Check if both responses are ok
      if (!japaneseResponse.ok || !englishResponse.ok) {
        throw new Error("Failed to fetch model details");
      }

      // Parse both responses
      const [japaneseData, englishData] = await Promise.all([
        japaneseResponse.json(),
        englishResponse.json(),
      ]);

      console.log("Model details response (Japanese):", japaneseData);
      console.log("Model details response (English):", englishData);

      // Store the complete data
      const combinedData = {
        japanese: japaneseData,
        english: englishData,
      };
      setModelDetailsData(combinedData);

      // Process Japanese data
      if (japaneseData && japaneseData.model_info && Array.isArray(japaneseData.model_info)) {
        // Extract unique Japanese countries
        const uniqueCountriesJP = [...new Set(japaneseData.model_info.map(item => item.country))];
        const countriesDataJP = uniqueCountriesJP.map((country, index) => ({
          id: index + 1,
          name: country,
          code: country.substring(0, 2).toUpperCase(),
        }));
        setDynamicCountriesJP(countriesDataJP);

        // Extract Japanese month/year combinations
        const monthYearCombinationsJP = japaneseData.model_info.map(item => ({
          month: item.publication_month - 1, // Convert to 0-indexed
          year: item.publication_year,
          country: item.country,
        }));
        setDynamicMonthYearsJP(monthYearCombinationsJP);

        console.log("Japanese countries:", countriesDataJP);
        console.log("Japanese month/year:", monthYearCombinationsJP);
      }

      // Process English data
      if (englishData && englishData.model_info && Array.isArray(englishData.model_info)) {
        // Extract unique English countries
        const uniqueCountriesEN = [...new Set(englishData.model_info.map(item => item.country))];
        const countriesDataEN = uniqueCountriesEN.map((country, index) => ({
          id: index + 1,
          name: country,
          code: country.substring(0, 2).toUpperCase(),
        }));
        setDynamicCountriesEN(countriesDataEN);

        // Extract English month/year combinations
        const monthYearCombinationsEN = englishData.model_info.map(item => ({
          month: item.publication_month - 1, // Convert to 0-indexed
          year: item.publication_year,
          country: item.country,
        }));
        setDynamicMonthYearsEN(monthYearCombinationsEN);

        console.log("English countries:", countriesDataEN);
        console.log("English month/year:", monthYearCombinationsEN);
      }

      // Create a mapping between Japanese and English country names
      if (japaneseData && japaneseData.model_info && englishData && englishData.model_info) {
        const mapping = {};

        // Group by index to match Japanese and English country names
        japaneseData.model_info.forEach((jpItem, index) => {
          const enItem = englishData.model_info[index];
          if (jpItem && enItem) {
            // Map Japanese -> English
            mapping[jpItem.country] = {
              jp: jpItem.country,
              en: enItem.country,
            };
            // Map English -> Japanese (reverse mapping)
            mapping[enItem.country] = {
              jp: jpItem.country,
              en: enItem.country,
            };
          }
        });

        setCountryNameMapping(mapping);
        console.log("Country name mapping:", mapping);
      }

      // Return both data sets
      return combinedData;
    } catch (error) {
      console.error("Error fetching model details:", error);
      return null;
    }
  };

  // Handle model suggestion selection
  const handleModelSuggestionClick = async (model) => {
    const event = {
      target: { value: model },
    };
    handleModelInputChange(event);
    setShowModelSuggestions(false);
    setModelSuggestions([]);

    // Mark as selected since user picked from list
    setIsModelSelected(true);
    setNoModelResultsFound(false);

    // Since user selected from list, validation passes
    if (onValidationChange) {
      onValidationChange(false);
    }

    // Reset previously selected values when a new model is selected
    setSelectedPublication(null);
    setSelectedCountry(null);
    setSelectedMonth(null);
    setSelectedYear(null);

    console.log("Model selected - Reset publication, country, and calendar values");

    // Call the API to fetch model details
    await fetchModelDetails(model);

    // Focus back on input
    if (modelInputRef.current) {
      modelInputRef.current.focus();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the publication dropdown
      if (
        publicationDropdownRef.current &&
        !publicationDropdownRef.current.contains(event.target) &&
        publicationDropdownOpen
      ) {
        setPublicationDropdownOpen(false);
      }

      // Check if click is outside the country dropdown
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target) &&
        countryDropdownOpen
      ) {
        setCountryDropdownOpen(false);
      }

      // Check if click is outside the group dropdown
      if (
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(event.target) &&
        groupDropdownOpen
      ) {
        setGroupDropdownOpen(false);
      }

      // Check if click is outside the subGroup dropdown
      if (
        subGroupDropdownRef.current &&
        !subGroupDropdownRef.current.contains(event.target) &&
        subGroupDropdownOpen
      ) {
        setSubGroupDropdownOpen(false);
      }

      // Check if click is outside the model suggestions
      if (
        modelSuggestionsRef.current &&
        !modelSuggestionsRef.current.contains(event.target) &&
        modelInputRef.current &&
        !modelInputRef.current.contains(event.target) &&
        showModelSuggestions
      ) {
        setShowModelSuggestions(false);
      }

      // Check if click is outside the VIN suggestions
      if (
        vinSuggestionsRef.current &&
        !vinSuggestionsRef.current.contains(event.target) &&
        vinInputRef.current &&
        !vinInputRef.current.contains(event.target) &&
        showVinSuggestions
      ) {
        setShowVinSuggestions(false);
      }

      // Check if click is outside the publication number suggestions
      if (
        publicationNumberSuggestionsRef.current &&
        !publicationNumberSuggestionsRef.current.contains(event.target) &&
        publicationNumberInputRef.current &&
        !publicationNumberInputRef.current.contains(event.target) &&
        showPublicationNumberSuggestions
      ) {
        setShowPublicationNumberSuggestions(false);
      }
    };

    // Add event listener when any dropdown is open
    if (
      publicationDropdownOpen ||
      countryDropdownOpen ||
      groupDropdownOpen ||
      subGroupDropdownOpen ||
      showModelSuggestions ||
      showVinSuggestions ||
      showPublicationNumberSuggestions
    ) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    publicationDropdownOpen,
    countryDropdownOpen,
    groupDropdownOpen,
    subGroupDropdownOpen,
    showModelSuggestions,
    showVinSuggestions,
    showPublicationNumberSuggestions,
    setGroupDropdownOpen,
    setSubGroupDropdownOpen,
  ]);

  useEffect(() => {
    // Sync the local publicationNumberInput with the parent's publicationNumber state
    // This is needed when the component re-renders with preserved values
    if (window.searchParametersCallback) {
      const data = {
        selectedPublication,
        selectedCountry,
        selectedMonth,
        selectedYear,
        publicationNumberInput,
      };
      window.searchParametersCallback(data);
    }
  }, [
    selectedPublication,
    selectedCountry,
    selectedMonth,
    selectedYear,
    publicationNumberInput,
  ]);

  useEffect(() => {
    // When component mounts or cycleId changes, check if we should preserve values
    if (preserveValues && window.getSearchParametersCallback) {
      const parentData = window.getSearchParametersCallback();
      if (parentData) {
        if (parentData.publicationNumber && !publicationNumberInput) {
          setPublicationNumberInput(parentData.publicationNumber);
        }
        if (parentData.selectedPublication && !selectedPublication) {
          setSelectedPublication(parentData.selectedPublication);
        }
        if (parentData.selectedCountry && !selectedCountry) {
          setSelectedCountry(parentData.selectedCountry);
        }
        if (parentData.selectedMonth !== null && selectedMonth === null) {
          setSelectedMonth(parentData.selectedMonth);
        }
        if (parentData.selectedYear !== null && selectedYear === null) {
          setSelectedYear(parentData.selectedYear);
        }
      }
    }
  }, [cycleId, preserveValues]);

  // Check if the current view is mobile on mount and when window resizes
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    // console.log(`Action triggered in cycle: ${cycleId}`);
    return () => {};
  }, [cycleId]);

  // Get current language - computed value, no state needed
  const currentLanguage = t.i18n?.language || localStorage.getItem('selectedLanguage') || 'jp';

  // Get the appropriate country and month/year lists based on current language
  const dynamicCountries = currentLanguage === 'jp' ? dynamicCountriesJP : dynamicCountriesEN;
  const dynamicMonthYears = currentLanguage === 'jp' ? dynamicMonthYearsJP : dynamicMonthYearsEN;

  const handleGroupClick = () => {
    if (isMobile) {
      setGroupModalOpen(true);
    } else {
      setGroupDropdownOpen(!groupDropdownOpen);
      // Close other dropdowns when opening this one
      setPublicationDropdownOpen(false);
      setCountryDropdownOpen(false);
      setSubGroupDropdownOpen(false);
    }
  };

  const handleSubGroupClick = () => {
    if (selectedGroup && !isLoadingSubGroups) {
      if (isMobile) {
        setSubGroupModalOpen(true);
      } else {
        setSubGroupDropdownOpen(!subGroupDropdownOpen);
        // Close other dropdowns when opening this one
        setPublicationDropdownOpen(false);
        setCountryDropdownOpen(false);
        setGroupDropdownOpen(false);
      }
    }
  };

  const handlePublicationClick = () => {
    if (isMobile) {
      setPublicationModalOpen(true);
    } else {
      setPublicationDropdownOpen(!publicationDropdownOpen);
      // Close other dropdowns when opening this one
      setCountryDropdownOpen(false);
      setGroupDropdownOpen(false);
      setSubGroupDropdownOpen(false);
    }
  };

  const handlePublicationSelect = (publication) => {
    setSelectedPublication(publication);
    setPublicationDropdownOpen(false);
    setPublicationModalOpen(false);
    if (onSelectionChange) {
      onSelectionChange();
    }
  };

  const handleCountryClick = () => {
    if (isMobile) {
      setCountryModalOpen(true);
    } else {
      setCountryDropdownOpen(!countryDropdownOpen);
      // Close other dropdowns when opening this one
      setPublicationDropdownOpen(false);
      setGroupDropdownOpen(false);
      setSubGroupDropdownOpen(false);
    }
  };

  const handleCountrySelect = (country) => {
    // Enhanced country object with both JP and EN names
    const enhancedCountry = {
      ...country,
      nameJP: countryNameMapping[country.name]?.jp || country.name,
      nameEN: countryNameMapping[country.name]?.en || country.name,
    };

    setSelectedCountry(enhancedCountry);
    setCountryDropdownOpen(false);
    setCountryModalOpen(false);

    if (onSelectionChange) {
      onSelectionChange();
    }
  };

  const handleCalendarClick = () => {
    setCalendarModalOpen(true);
  };

  // Helper function to get country name in current language
  const getCountryNameInCurrentLanguage = (country) => {
    if (!country) return "";

    // If we have a mapping, use it
    if (countryNameMapping[country.name]) {
      return countryNameMapping[country.name][currentLanguage] || country.name;
    }

    // Otherwise return the original name
    return country.name;
  };

  // Get available months and years based on selected country and month
  const getAvailableMonthsYears = () => {
    if (dynamicMonthYears.length === 0) {
      return { availableMonths: null, availableYears: null };
    }

    // Filter by selected country if one is selected
    let filteredData = dynamicMonthYears;
    if (selectedCountry) {
      // Get the country name in the current data language
      const countryNameToFilter = countryNameMapping[selectedCountry.name]?.[currentLanguage] || selectedCountry.name;

      filteredData = dynamicMonthYears.filter(
        item => item.country === countryNameToFilter
      );
    }

    // Extract unique months
    const uniqueMonths = [...new Set(filteredData.map(item => item.month))].sort((a, b) => a - b);

    // Filter years based on selected month
    let uniqueYears;
    if (selectedMonth !== null) {
      // Only show years that are available for the selected month
      const yearsForSelectedMonth = filteredData
        .filter(item => item.month === selectedMonth)
        .map(item => item.year);
      uniqueYears = [...new Set(yearsForSelectedMonth)].sort((a, b) => b - a);
    } else {
      // If no month selected, show all available years
      uniqueYears = [...new Set(filteredData.map(item => item.year))].sort((a, b) => b - a);
    }

    return {
      availableMonths: uniqueMonths.length > 0 ? uniqueMonths : null,
      availableYears: uniqueYears.length > 0 ? uniqueYears : null,
    };
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);

    // Clear selected year when month changes, as available years will be filtered
    // Only clear if the currently selected year is not available for this month
    if (selectedYear !== null) {
      // Filter by country first if selected
      let filteredData = dynamicMonthYears;
      if (selectedCountry) {
        const countryNameToFilter = countryNameMapping[selectedCountry.name]?.[currentLanguage] || selectedCountry.name;
        filteredData = dynamicMonthYears.filter(
          item => item.country === countryNameToFilter
        );
      }

      // Check if current year will be available after this month selection
      const yearsForNewMonth = filteredData
        .filter(item => item.month === month)
        .map(item => item.year);

      if (!yearsForNewMonth.includes(selectedYear)) {
        setSelectedYear(null);
      }
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
  };

  const handleCalendarClose = () => {
    setCalendarModalOpen(false);
    if (onSelectionChange) {
      onSelectionChange();
    }
  };

  // Handle publication number input change with autocomplete
  const handlePublicationNumberChangeWithSuggestions = (e) => {
  const value = e.target.value;
  setPublicationNumberInput(value);

  if (onSelectionChange) {
    onSelectionChange();
  }

  // Reset validation states but don't show error immediately
  setIsPublicationNumberSelected(false);
  setNoPublicationNumberResultsFound(false);
  
  // Only set validation to true if input is empty
  if (onValidationChange) {
    onValidationChange(value.trim().length === 0);
  }

  // Fetch suggestions from API (this will handle validation inside)
  fetchPublicationNumberSuggestions(value);
};


  // Handle publication number suggestion selection
  const handlePublicationNumberSuggestionClick = (pubNumber) => {
    // The response contains simple strings, so we can use it directly
    setPublicationNumberInput(pubNumber);
    setShowPublicationNumberSuggestions(false);
    setNoPublicationNumberResultsFound(false);

    // Mark as selected since user picked from list
    setIsPublicationNumberSelected(true);

    // Since user selected from list, validation passes
    if (onValidationChange) {
      onValidationChange(false);
    }

    // Focus back on input
    if (publicationNumberInputRef.current) {
      publicationNumberInputRef.current.focus();
    }
  };

  const resetAllLocalStates = (shouldPreserveValues = preserveValues) => {
    // If preserving values, get current values from parent before resetting
    let currentPublicationNumber = "";
    let currentSelectedPublication = null;
    let currentSelectedCountry = null;
    let currentSelectedMonth = null;
    let currentSelectedYear = null;

    if (shouldPreserveValues && window.getSearchParametersCallback) {
      const parentData = window.getSearchParametersCallback();
      if (parentData) {
        currentPublicationNumber = parentData.publicationNumber || "";
        currentSelectedPublication = parentData.selectedPublication;
        currentSelectedCountry = parentData.selectedCountry;
        currentSelectedMonth = parentData.selectedMonth;
        currentSelectedYear = parentData.selectedYear;
      }
    }

    // Only reset if not preserving values
    if (!shouldPreserveValues) {
      setPublicationNumberInput("");
      setSelectedPublication(null);
      setSelectedCountry(null);
      setSelectedMonth(null);
      setSelectedYear(null);
    } else {
      // Restore preserved values
      setPublicationNumberInput(currentPublicationNumber);
      setSelectedPublication(currentSelectedPublication);
      setSelectedCountry(currentSelectedCountry);
      setSelectedMonth(currentSelectedMonth);
      setSelectedYear(currentSelectedYear);
    }

    // Always reset modal states
    setPublicationDropdownOpen(false);
    setCountryDropdownOpen(false);
    setCalendarModalOpen(false);
    setGroupModalOpen(false);
    setSubGroupModalOpen(false);
    setPublicationModalOpen(false);
    setCountryModalOpen(false);

    // Reset suggestion states for VIN and Model
    setVinSuggestions([]);
    setShowVinSuggestions(false);
    setNoVinResultsFound(false);
    setIsVinSelected(false);
    setModelSuggestions([]);
    setShowModelSuggestions(false);
    setNoModelResultsFound(false);
    setIsModelSelected(false);

    // Only reset publication number suggestions if not preserving values OR if no publication number
    if (!shouldPreserveValues || !currentPublicationNumber.trim()) {
      setPublicationNumberSuggestions([]);
      setShowPublicationNumberSuggestions(false);
      setNoPublicationNumberResultsFound(false);
      setIsPublicationNumberSelected(false);
    }

    // Handle validation
    if (!shouldPreserveValues && onValidationChange) {
      onValidationChange(true);
    } else if (
      shouldPreserveValues &&
      currentPublicationNumber.trim() &&
      onValidationChange
    ) {
      // If preserving values and publication number exists, validate it
      setTimeout(() => {
        fetchPublicationNumberSuggestions(currentPublicationNumber);
      }, 100);
    }
  };

  const handleVinSearchClick = () => {
    setSearchType("vin");
    resetSearchState(false); // Don't preserve when switching search types
    resetAllLocalStates(false); // Don't preserve when switching search types
  };

  const handleModelSearchClick = () => {
    setSearchType("model");
    resetSearchState(false); // Don't preserve when switching search types
    resetAllLocalStates(false); // Don't preserve when switching search types
  };

  const handlePublicationSearchClick = () => {
    setSearchType("publication");
    resetSearchState(false); // Don't preserve when switching search types
    resetAllLocalStates(false); // Don't preserve when switching search types
  };

  return (
    <div key={instanceId} className="fusoChatFirst queryMarkPnc">
      <img src={fusoChat} alt="FUSO" className="w-6 h-6 fusoLogoSmall" />
      <div className="bg-red-50 p-4 pncChatFirst custom-radius">
        <div className="flex gap-3">
          <div className="flex-1">
            <p className="gap-[4px] searchHeadPnc">
              <span>{t("choosePara")}</span>
              <img
                className="targetIconPnc"
                src={searchPnc}
                alt="Search Icon"
              />
            </p>

            {/* Search Type Selector */}
            <div className="mt-4 space-y-4">
              <div className="flex gap-[16px] selectPartShop flex-wrap">
                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 vinBtnPncNew vinBtnShopNew ${
                    searchType === "vin"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                  }`}
                  style={
                    searchType === "vin"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={handleVinSearchClick}
                >
                  {t("vinChassisSearch")}
                </button>
                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 classificationPncNew modelShopNew ${
                    searchType === "model"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                  }`}
                  style={
                    searchType === "model"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={handleModelSearchClick}
                >
                  {t("modalSearchShop")}
                </button>

                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 classificationPncNew modelShopNew ${
                    searchType === "publication"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                  }`}
                  style={
                    searchType === "publication"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={handlePublicationSearchClick}
                >
                  {t("searchPublication")}
                </button>
              </div>

              {/* VIN Input */}
              {searchType === "vin" && (
                <div className="grid gap-4">
                  <p className="flex vinBoxNew">{t("enterChassisMsg")}</p>
                  <div className="flex gap-[16px] vinSelectionShop flex-wrap">
                    <div className="relative flex-1">
                      <input
                        ref={vinInputRef}
                        type="text"
                        placeholder={t("enterChassisProMsg")}
                        value={vinInput}
                        onChange={handleVinInputChangeWithSuggestions}
                        className="rounded-lg px-4 py-2 vinInsertNew customInputShop w-full flex-wrap max-w-[342px]"
                        autoComplete="off"
                      />

                      {/* VIN Validation Message */}
                      {noVinResultsFound &&
  vinInput.trim().length >= 1 &&
  !isLoadingVinSuggestions && (
    <div className="text-red-500 text-sm mt-1 px-1 max-w-[340px]">
      {t("noVinFound")}
    </div>
  )}

                      {/* Add this new validation message for when VIN doesn't match suggestions */}
                      {/* {!noVinResultsFound &&
                        vinSuggestions.length > 0 &&
                        vinInput.trim().length >= 1 &&
                        !isVinSelected &&
                        !isLoadingVinSuggestions && (
                          <div className="text-red-500 text-sm mt-1 px-1 max-w-[340px]">
                            {t("vinNotMatched")}
                          </div>
                        )} */}

                      {/* VIN suggestions dropdown */}
                      {showVinSuggestions && vinSuggestions.length > 0 && (
                        <div
                          ref={vinSuggestionsRef}
                          className="absolute mb-1 left-0 right-0 bottom-full mt-1 bg-white border border-gray-300 rounded-lg max-h-[10rem] overflow-y-auto z-30 shadow-md max-w-[342px]"
                        >
                          {isLoadingVinSuggestions ? (
                            <div className="px-4 py-2 text-gray-500">
                               {t('loading')}
                            </div>
                          ) : (
                            vinSuggestions.map((vin, index) => (
                              <div
                                key={`vin-suggestion-${index}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b last:border-b-0"
                                onClick={() => handleVinSuggestionClick(vin)}
                              >
                                {typeof vin === "string" ? (
                                  <div className="font-medium">{vin}</div>
                                ) : (
                                  <>
                                    <div className="font-medium">
                                      {vin.vinNumber ||
                                        vin.vin ||
                                        vin.chassis_no ||
                                        vin.chassis_number ||
                                        JSON.stringify(vin)}
                                    </div>
                                    {(vin.model || vin.veh_model) && (
                                      <div className="text-sm text-gray-600">
                                        Model: {vin.model || vin.veh_model}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    <div ref={publicationDropdownRef}>
                      <div className="border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative min-w-[264px] customInputShop">
                        <div
                          className="w-full cursor-pointer flex justify-between items-center gap-[60px] whitespace-nowrap"
                          onClick={handlePublicationClick}
                        >
                          <span>
                            {selectedPublication
                              ? t(selectedPublication.name)
                              : t("Select Publication Type")}
                          </span>
                          <img src={dropPnc} alt="Dropdown Icon" />
                        </div>

                        {/* Desktop Dropdown */}
                        {!isMobile && publicationDropdownOpen && (
                          <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-300 rounded-lg w-full max-h-40 overflow-y-auto z-10 shadow-md dropdownStylePnc">
                            {publicationTypes.map((pub) => (
                              <div
                                key={`${cycleId}-${pub.name}-${pub.id}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer"
                                onClick={() => handlePublicationSelect(pub)}
                              >
                                {t(pub.name)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Model/Classification Input */}
              {searchType === "model" && (
                <>
                  <p className="flex vinBoxNew">{t("enterModelMsg")}</p>
                  <div className="grid grid-cols-2 gap-4 vinSelectionShop customInputShop flex-wrap smallWrapShop">
                    <div className="relative">
                      <input
                        ref={modelInputRef}
                        type="text"
                        placeholder={t("enterModel")}
                        value={modelInput}
                        onChange={handleModelInputChangeWithSuggestions}
                        onPaste={handleModelPaste}
                        onClick={handleModelInputClick}
                        className="border border-gray-300 rounded-lg px-4 py-2 vinInsertNew w-full customInputShop"
                        autoComplete="off"
                      />

                      {noModelResultsFound && 
  modelInput.trim().length >= 1 && (
    <div className="text-red-500 text-sm mt-1 px-1 max-w-[340px]">
      {t("noModelFound")}
    </div>
  )}

                      {/* {!noModelResultsFound &&
                        modelSuggestions.length > 0 &&
                        modelInput.trim().length >= 1 &&
                        !isModelSelected && (
                          <div className="text-red-500 text-sm mt-1 px-1 max-w-[340px]">
                            {t("modelNotMatched")}
                          </div>
                        )} */}

                      {/* Model suggestions dropdown */}
                      {showModelSuggestions && modelSuggestions.length > 0 && (
                        <div
                          ref={modelSuggestionsRef}
                          className="absolute left-0 right-0 bottom-full mt-1 bg-white border border-gray-300 rounded-lg max-h-40 overflow-y-auto z-20 shadow-md"
                        >
                          {isLoadingModelSuggestions ? (
                            <div className="px-4 py-2 text-gray-500">
                              {t('loading')}
                            </div>
                          ) : (
                            modelSuggestions.map((model, index) => (
                              <div
                                key={`model-suggestion-${index}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b last:border-b-0"
                                onClick={() => handleModelSuggestionClick(model)}
                              >
                                {typeof model === "string" ? model : String(model)}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    <div
                      ref={publicationDropdownRef}
                      className="border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative max-h-[42px]"
                    >
                      <div
                        className="w-full cursor-pointer flex justify-between items-center whitespace-nowrap"
                        onClick={handlePublicationClick}
                      >
                        <span>
                          {selectedPublication
                            ? t(selectedPublication.name)
                            : t("Select Publication Type")}
                        </span>
                        <img src={dropPnc} alt="Dropdown Icon" />
                      </div>

                      {/* Desktop Dropdown */}
                      {!isMobile && publicationDropdownOpen && (
                        <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-300 rounded-lg w-full max-h-40 overflow-y-auto z-10 shadow-md dropdownStylePnc">
                          {publicationTypes.map((pub) => (
                            <div
                              key={`${cycleId}-${pub.name}-${pub.id}`}
                              className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer"
                              onClick={() => handlePublicationSelect(pub)}
                            >
                              {t(pub.name)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div
                      ref={countryDropdownRef}
                      className="border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative"
                    >
                      <div
                        className="w-full cursor-pointer flex justify-between items-center whitespace-nowrap"
                        onClick={handleCountryClick}
                      >
                        <span>
                          {selectedCountry
                            ? getCountryNameInCurrentLanguage(selectedCountry)
                            : t("SelectsCountry")}
                        </span>
                        <img src={dropPnc} alt="Dropdown Icon" />
                      </div>

                      {/* Desktop Dropdown */}
                      {!isMobile && countryDropdownOpen && (
                        <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-300 rounded-lg w-full max-h-60 overflow-y-auto z-10 shadow-md dropdownStylePnc">
                          {dynamicCountries.length > 0 ? (
                            dynamicCountries.map((country) => (
                              <div
                                key={`${cycleId}-${country.name}-${country.code}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer"
                                onClick={() => handleCountrySelect(country)}
                              >
                                {country.name}
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-gray-500 text-center">
                              {t("Please select a model first")}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Calendar Month/Year Button */}
                    <div className="border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative">
                      <div
                        className="w-full cursor-pointer flex justify-between items-center whitespace-nowrap"
                        onClick={handleCalendarClick}
                      >
                        <span>
                          {selectedMonth !== null && selectedYear !== null
                            ? `${t(months[selectedMonth])} ${selectedYear}`
                            : t("Select Month/Year")}
                        </span>
                        <img src={dropPnc} alt="Dropdown Icon" />
                      </div>
                    </div>
                  </div>
                  {isLoadingModelClass && (
                    <p className="text-gray-500 text-sm mt-2"> {t("loading")} </p>
                  )}
                  {/* {!isLoadingModelClass && modelClassError && (
                    <p className="text-red-500 text-sm mt-2">
                      {modelClassError}
                    </p>
                  )} */}
                </>
              )}

              {/* Publication Number Input */}
              {searchType === "publication" && (
                <div className="grid gap-4">
                  <p className="flex vinBoxNew">
                    {t("enterPublicationNumber")}
                  </p>
                  <div className="flex gap-[16px] max-w-[342px]">
                    <div className="relative flex-1">
                      <input
                        ref={publicationNumberInputRef}
                        type="text"
                        placeholder={t("publicationNo")}
                        value={publicationNumberInput}
                        onChange={handlePublicationNumberChangeWithSuggestions}
                        className="rounded-lg px-4 py-2 vinInsertNew customInputShop w-full"
                        autoComplete="off"
                      />

                      {/* Publication Number Validation Message */}
                      {noPublicationNumberResultsFound &&
  publicationNumberInput.trim().length >= 1 &&
  !isLoadingPublicationNumberSuggestions && (
    <div className="text-red-500 text-sm mt-1 px-1 max-w-[340px]">
      {t("noPublicationNumberFound")}
    </div>
  )}

                      {/* Add this new validation message for when Publication Number doesn't match suggestions */}
                      {/* {!noPublicationNumberResultsFound &&
                        publicationNumberSuggestions.length > 0 &&
                        publicationNumberInput.trim().length >= 1 &&
                        !isPublicationNumberSelected &&
                        !isLoadingPublicationNumberSuggestions && (
                          <div className="text-red-500 text-sm mt-1 px-1 max-w-[340px]">
                            {t("publicationNumberNotMatched")}
                          </div>
                        )} */}

                      {/* Publication Number suggestions dropdown */}
                      {showPublicationNumberSuggestions &&
                        publicationNumberSuggestions.length > 0 && (
                          <div
                            ref={publicationNumberSuggestionsRef}
                            className="absolute mb-1 left-0 right-0 bottom-full mt-1 bg-white border border-gray-300 rounded-lg max-h-[10rem] overflow-y-auto z-30 shadow-md max-w-[342px]"
                          >
                            {isLoadingPublicationNumberSuggestions ? (
                              <div className="px-4 py-2 text-gray-500">
                                {t('loading')}
                              </div>
                            ) : (
                              publicationNumberSuggestions.map(
                                (pubNumber, index) => (
                                  <div
                                    key={`publication-number-suggestion-${index}`}
                                    className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b last:border-b-0"
                                    onClick={() =>
                                      handlePublicationNumberSuggestionClick(
                                        pubNumber
                                      )
                                    }
                                  >
                                    <div className="font-medium">
                                      {pubNumber}
                                    </div>
                                  </div>
                                )
                              )
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Group Selection Modal */}
      {isMobile && groupModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">Select Group</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setGroupModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {groups &&
                groups.map((group) => (
                  <div
                    key={`${cycleId}-${group.description}-${group.main_group}`}
                    className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b"
                    onClick={() => {
                      handleGroupSelect(
                        `${group.group_name}|${group.main_group}`
                      );
                      setGroupModalOpen(false);
                    }}
                  >
                    {group.main_group} - {group.group_name}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sub-Group Selection Modal */}
      {isMobile && subGroupModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">Select Sub Group</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setSubGroupModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {subGroups &&
                subGroups.map((subGroup) => (
                  <div
                    key={`${cycleId}-${subGroup.description}-${subGroup.sub_group}`}
                    className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b"
                    onClick={() => {
                      handleSubGroupSelect(
                        `${subGroup.sub_group}|${subGroup.group_name}`
                      );
                      setSubGroupModalOpen(false);
                    }}
                  >
                    {subGroup.sub_group} - {subGroup.group_name}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Calendar Selection Modal for both desktop and mobile */}
      {calendarModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">{t("Select Month/Year")}</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={handleCalendarClose}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-lg font-medium text-gray-700 mb-3">
                  {t("Month")}
                </label>
                <div class="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-40 overflow-y-auto sm:max-h-none sm:overflow-visible">
                  {months.map((month, index) => {
                    const { availableMonths } = getAvailableMonthsYears();
                    const isAvailable = !availableMonths || availableMonths.includes(index);
                    return (
                      <div
                        key={`calendar-month-${index}`}
                        className={`py-3 text-center rounded-lg border ${
                          selectedMonth === index
                            ? "bg-[#FFE0E0] text-red-500 border-red-500 font-medium"
                            : isAvailable
                            ? "hover:bg-slate-100 border-gray-300 cursor-pointer"
                            : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                        }`}
                        onClick={() => isAvailable && handleMonthSelect(index)}
                      >
                        {t(month)}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-lg font-medium text-gray-700 mb-3">
                  {t("Year")}
                </label>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-40 overflow-y-auto">
                  {(() => {
                    const { availableYears } = getAvailableMonthsYears();
                    const yearsToShow = availableYears || years;
                    return yearsToShow.map((year) => {
                      const isAvailable = !availableYears || availableYears.includes(year);
                      return (
                        <div
                          key={`calendar-year-${year}`}
                          className={`py-3 text-center rounded-lg border ${
                            selectedYear === year
                              ? "bg-[#FFE0E0] text-red-500 border-red-500 font-medium"
                              : isAvailable
                              ? "hover:bg-slate-100 border-gray-300 cursor-pointer"
                              : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          }`}
                          onClick={() => isAvailable && handleYearSelect(year)}
                        >
                          {year}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium text-lg"
                  onClick={handleCalendarClose}
                >
                  {t("Done")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Publication Selection Modal */}
      {isMobile && publicationModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">
                {t("Select Publication Type")}{" "}
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setPublicationModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {publicationTypes.map((pub) => (
                <div
                  key={`${cycleId}-${pub.name}-${pub.id}`}
                  className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b"
                  onClick={() => handlePublicationSelect(pub)}
                >
                  {t(pub.name)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Country Selection Modal */}
      {isMobile && countryModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium"> {t('SelectCountry')}</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setCountryModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {dynamicCountries.length > 0 ? (
                dynamicCountries.map((country) => (
                  <div
                    key={`${cycleId}-${country.name}-${country.code}`}
                    className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b"
                    onClick={() => handleCountrySelect(country)}
                  >
                    {country.name}
                  </div>
                ))
              ) : (
                <div className="p-4 text-gray-500 text-center">
                  {t("Please select a model first")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchParameters;
